import { ThreadViewProvider, ThreadViewProviderProps } from '@hb/react'
import {
  createContext,
  createRef,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import { THREAD_LIST_HEIGHT } from './constants'

export interface WebThreadViewContextData {
  messageListRef: React.RefObject<HTMLDivElement | null>
  containerRef: React.RefObject<HTMLDivElement | null>
  bodyRef: React.RefObject<HTMLDivElement | null>
  messageListHeight: number
  inPopover?: boolean
  bodyHeight: number
}
const WebThreadViewContext = createContext<WebThreadViewContextData>({
  messageListRef: createRef(),
  containerRef: createRef(),
  bodyRef: createRef(),
  messageListHeight: 0,
  inPopover: false,
  bodyHeight: 0,
})

export type WebThreadViewProviderProps = PropsWithChildren<
  ThreadViewProviderProps & {
    messageListRef: React.RefObject<HTMLDivElement | null>
    bodyRef: React.RefObject<HTMLDivElement | null>
    containerRef: React.RefObject<HTMLDivElement | null>
    inPopover?: boolean
  }
>

export const WebThreadViewProviderBody = ({
  messageListRef,
  bodyRef,
  containerRef,
  children,
  inPopover,
  ...props
}: PropsWithChildren<WebThreadViewProviderProps>) => {
  const { isOpen } = props
  const mounted = useRef(false)
  const { height: bodyHeight } = useResizeObserver(bodyRef, 'content')
  const [messageListHeight, setMessageListHeight] = useState(THREAD_LIST_HEIGHT)
  useEffect(() => {
    if (isOpen && containerRef.current) {
      // check if box is off screen
      const rect = containerRef.current.getBoundingClientRect()
      if (rect.bottom > window.innerHeight) {
        setMessageListHeight(window.innerHeight - rect.top - 100)
      } else {
        setMessageListHeight(THREAD_LIST_HEIGHT)
      }
    } else {
      mounted.current = false
    }
  }, [isOpen, containerRef])

  const contextValue = useMemo<WebThreadViewContextData>(
    () => ({ messageListRef, bodyHeight, messageListHeight, containerRef, bodyRef, inPopover }),
    [messageListRef, bodyHeight, messageListHeight, containerRef, bodyRef, inPopover],
  )

  return (
    <WebThreadViewContext.Provider value={contextValue}>{children}</WebThreadViewContext.Provider>
  )
}

export const WebThreadViewProvider = ({
  children,
  messageListRef,
  bodyRef,
  containerRef,
  ...props
}: PropsWithChildren<WebThreadViewProviderProps>) => (
  <ThreadViewProvider {...props}>
    <WebThreadViewProviderBody
      messageListRef={messageListRef}
      bodyRef={bodyRef}
      containerRef={containerRef}
      {...props}>
      {children}
    </WebThreadViewProviderBody>
  </ThreadViewProvider>
)

export const useWebThreadView = () => useContext(WebThreadViewContext)
